import { useQuery } from '@apollo/client'

import type { LandingBlockProps } from '@features/landing-page-builder/model/blockRegistry'
import { GQL_LANDING_SALE_PRODUCTS } from '@features/landing-page-builder/model/productQueries'
import type { LandingProductsPayload } from '@features/landing-page-builder/model/resolveProductsQuery'
import { resolveProductsQuery } from '@features/landing-page-builder/model/resolveProductsQuery'
import LandingProductGroupsBlock from '@features/landing-page-builder/ui/blocks/LandingProductGroupsBlock'
import {
  ProductsCategoryCarousel,
  ProductsGroupCarousel,
  ProductsPreviouslyOrderedCarousel,
} from '@features/products-carousel'

import { ProductCarousel } from '@widgets/product-carousel'

import type { Product } from '@shared/generated'

import styles from './LandingProductsBlock.module.scss'

/*
  products: a catalog product section.

  Everything except `group` is payload-driven: the payload names a standard
  backend query (payload.query, e.g. popularProducts / saleProducts / products)
  plus how to narrow and page it, and this block issues exactly that request and
  renders the result in the shared product carousel. That means a new product
  section on a landing is a backend config change, not a frontend release.

  Some subtypes name an entity by payload.id instead of describing a query —
  `group` (готовые подборки) and `category`. Those carry no payload.query, so
  they are delegated to the same carousels the main page uses rather than going
  through the query resolver, which would find nothing to run and render nothing.

  `group` additionally has a list form: a `groups` array becomes a row of
  collection cards instead of one group's products.
*/
export default function LandingProductsBlock (props: LandingBlockProps) {
  const { block, payload } = props
  const p = (payload ?? {}) as LandingProductsPayload

  if (block.subtype === 'group') {
    const hasGroupList = Array.isArray((payload as { groups?: unknown } | null)?.groups)
    const groupId = p.id != null ? String(p.id) : ''

    if (!hasGroupList && !groupId) {
      return null
    }

    return (
      <div className={styles.root}>
        {hasGroupList
          ? <LandingProductGroupsBlock {...props} />
          // The carousel titles itself from the group's own label; payload.name
          // is the section name the admin typed and is not forced over it.
          : <ProductsGroupCarousel id={groupId} />}
      </div>
    )
  }

  /*
    A category section: payload.id is the category id (payload.categoryId is
    accepted too, since the products blocks spell it that way).
  */
  if (block.subtype === 'category') {
    const categoryId = p.id != null
      ? String(p.id)
      : (p.categoryId != null ? String(p.categoryId) : '')

    if (!categoryId) {
      return null
    }

    return (
      <div className={styles.root}>
        <ProductsCategoryCarousel categoryId={categoryId} />
      </div>
    )
  }

  // Personal section — the carousel resolves the current customer itself.
  if (block.subtype === 'previouslyOrdered') {
    return (
      <div className={styles.root}>
        <ProductsPreviouslyOrderedCarousel />
      </div>
    )
  }

  return <ProductsQuerySection {...props} />
}

function ProductsQuerySection ({ block, payload, shopId }: LandingBlockProps) {
  const p = (payload ?? {}) as LandingProductsPayload
  const resolved = resolveProductsQuery(block.subtype, p, shopId)

  const { data, loading } = useQuery<Record<string, { edges?: Product[] | null } | null>>(
    // A document is required even when skipped; the skip flag keeps it inert.
    resolved?.document ?? GQL_LANDING_SALE_PRODUCTS,
    {
      variables: resolved?.variables,
      skip: !resolved || !shopId,
      fetchPolicy: 'cache-and-network',
    },
  )

  if (!resolved) {
    return null
  }

  const products = (data?.[resolved.root]?.edges ?? []) as Product[]
  const title = p.name || p.title || p.tabLabel || ''

  if (!loading && products.length === 0) {
    return null
  }

  return (
    <div className={styles.root}>
      <ProductCarousel
        title={title}
        products={products}
        link={resolved.link}
        loading={loading && products.length === 0}
        listName={`Landing: ${title || block.subtype || 'products'}`}
        listId={block.id ?? undefined}
      />
    </div>
  )
}
